import { AvatarWithProgress } from "@circle-react/components/Leaderboard/Dashboard/MyProfile/AvatarWithProgress";
import { LevelBadge } from "@circle-react/components/Leaderboard/DashboardUIKit/MyProfile/LevelBadge";
import { useGamificationMember } from "@circle-react/components/Profile/HoverCard/useGamificationMember";
import { useGamificationSettings } from "@circle-react/components/SettingsApp/Gamification/gamificationApi";
import { Icon } from "@circleco/compass/components/Icon";
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { Typography } from "@circleco/compass/components/Typography";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { dateStringToTimeAgo } from "@/react/helpers/dateTimeHelpers/timeAgo";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { ActionButtons } from "./ActionButtons";
import type { ProfileHoverCardProps } from "./HoverCard";

interface CardContentCompassProps extends Omit<
  ProfileHoverCardProps,
  "children"
> {
  closeCard: () => void;
}

export const CardContentCompass = ({
  communityMemberId,
  publicUid,
  sqid,
  messagingEnabled,
  avatarURL,
  name = "",
  headline = "",
  userLastSeenAt,
  closeCard,
}: CardContentCompassProps) => {
  const { data: gamificationSettings } = useGamificationSettings();
  const shouldShowLevelBadge = !!gamificationSettings?.enabled;

  const { isPending, data } = useGamificationMember({
    member_ids: [communityMemberId],
    enabled: shouldShowLevelBadge,
  });

  const gamificationMember = useMemo(
    () =>
      data?.find(
        member =>
          Number(member.community_member_id) === Number(communityMemberId),
      ),
    [data, communityMemberId],
  );

  return (
    <div
      className={classNames(
        "flex justify-between flex-col gap-6 w-full",
        headline ? "items-start" : "items-center",
      )}
    >
      <div className="flex flex-col gap-5 w-full">
        {/* Name and avatar */}
        <div className="flex flex-row gap-4">
          <div className="flex flex-col gap-1 max-w-48">
            <Typography className="line-clamp-2" variant="heading-md">
              {name}
            </Typography>
            {headline && (
              <Typography
                className="line-clamp-2"
                variant="body-sm"
                color="secondary"
              >
                {headline}
              </Typography>
            )}
          </div>

          <div className="flex justify-end flex-1">
            {shouldShowLevelBadge && isPending ? (
              <Skeleton shape="rounded" className="size-16" />
            ) : (
              <AvatarWithProgress
                avatarUrl={avatarURL}
                name={name}
                size="small"
                progress={gamificationMember?.level_progress}
                level={gamificationMember?.current_level}
              />
            )}
          </div>
        </div>

        {/* Last seen */}
        {userLastSeenAt && (
          <div className="flex gap-2">
            <Icon name="clock" size="md" />
            <Typography variant="caption">
              {t("profile_drawer.last_seen", {
                last_seen: dateStringToTimeAgo(userLastSeenAt),
              })}
            </Typography>
          </div>
        )}

        {/* Level badge */}

        {shouldShowLevelBadge && (
          <>
            {isPending ? (
              <Skeleton shape="rounded" className="mb-2 h-7 w-32" />
            ) : (
              gamificationMember?.current_level && (
                <div>
                  <LevelBadge
                    className="mb-0"
                    level={gamificationMember?.current_level}
                    levelName={gamificationMember?.current_level_name}
                  />
                </div>
              )
            )}
          </>
        )}
      </div>

      <ActionButtons
        communityMemberId={communityMemberId}
        publicUid={publicUid}
        sqid={sqid}
        messagingEnabled={messagingEnabled}
        closeCard={closeCard}
      />
    </div>
  );
};
